import {
  PeopleRole,
  usePeopleDiscovery,
  usePeopleRankingPrompt,
  usePeopleRankingPromptDiff,
} from "@inrange/building-manager-api-client";
import { useState } from "react";
import { Button, ButtonGroup } from "react-bootstrap";
import CollapsibleCard from "../site/CollapsibleCard";
import PeopleRankingTestPanel from "./PeopleRankingTestPanel";
import PromptRuleSetEditor from "./PromptRuleSetEditor";

// The contacts-ranking persona is per-role (one "landlord" prompt, one "tenant"), so a
// role toggle drives which prompt the shared editor edits. The editor is keyed on the
// role, so switching remounts it - resetting its drafts, diff state and test panel to
// the newly-loaded role's prompt.
const PeopleRankingPromptCard = () => {
  const [role, setRole] = useState<PeopleRole>("landlord");

  const prompt = usePeopleRankingPrompt(role);
  const {
    savePeoplePromptRuleMut,
    deletePeoplePromptRuleMut,
    reorderPeoplePromptRulesMut,
    savePeoplePromptPreambleMut,
    savePeoplePromptAddendumMut,
    restorePeoplePromptRevisionMut,
  } = usePeopleDiscovery();

  const writing =
    savePeoplePromptRuleMut.isLoading ||
    deletePeoplePromptRuleMut.isLoading ||
    reorderPeoplePromptRulesMut.isLoading ||
    savePeoplePromptPreambleMut.isLoading ||
    savePeoplePromptAddendumMut.isLoading ||
    restorePeoplePromptRevisionMut.isLoading;

  // The people diff hook takes `role` first; bind it to the shared editor's
  // (from, to, enabled) diff-hook shape. A use-prefixed wrapper so it stays a hook.
  const useRoleDiff = (
    fromRevision: number | null,
    toRevision: number | null,
    enabled: boolean
  ) => usePeopleRankingPromptDiff(role, fromRevision, toRevision, enabled);

  return (
    <CollapsibleCard title="Contacts ranking prompt" initialOpen={false}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <span style={{ fontSize: 12, color: "#888" }}>Editing the</span>
        <ButtonGroup size="sm">
          <Button
            variant={role === "landlord" ? "primary" : "outline-primary"}
            onClick={() => setRole("landlord")}
          >
            Landlord
          </Button>
          <Button
            variant={role === "tenant" ? "primary" : "outline-primary"}
            onClick={() => setRole("tenant")}
          >
            Tenant
          </Button>
        </ButtonGroup>
        <span style={{ fontSize: 12, color: "#888" }}>
          contacts-ranking persona
        </span>
      </div>

      <PromptRuleSetEditor
        key={role}
        prompt={prompt}
        writing={writing}
        saveRuleLoading={savePeoplePromptRuleMut.isLoading}
        savePreambleLoading={savePeoplePromptPreambleMut.isLoading}
        compiledPreviewLabel="Compiled prompt (what the ranker sees)"
        loadFailedMessage={`Could not load the ${role} contacts-ranking prompt.`}
        ruleCategoryPlaceholder="e.g. Seniority, Function, Signals"
        description={
          <>
            The contacts-ranking pass reads the {role} company&apos;s people from
            Apollo and ranks them into bands (primary / also-strong / sustainability
            / other) using this editable persona: a <strong>preamble</strong> plus a
            set of addressable <strong>rules</strong> (each with why it exists, its
            limits and the evidence behind it). Every change appends a new revision;
            every discovered shortlist is stamped with the revision that produced it.
            The data-residency guardrail below is always appended and cannot be
            edited away.
          </>
        }
        emptyRulesHint={
          <>
            No rules on this revision - the whole prompt is in the preamble above.
            Use <strong>+ Add rule</strong> to break it out into addressable rules
            (each with its own why / limits / evidence), or{" "}
            <strong>Reset whole prompt to shipped default</strong> to adopt the
            shipped rule set.
          </>
        }
        onSaveRule={(a) => savePeoplePromptRuleMut.mutateAsync({ ...a, role })}
        onDeleteRule={(a) => deletePeoplePromptRuleMut.mutateAsync({ ...a, role })}
        onReorderRules={(a) =>
          reorderPeoplePromptRulesMut.mutateAsync({ ...a, role })
        }
        onSavePreamble={(a) =>
          savePeoplePromptPreambleMut.mutateAsync({ ...a, role })
        }
        onResetToDefault={(a) =>
          savePeoplePromptAddendumMut.mutateAsync({ ...a, role })
        }
        onRestoreRevision={(a) =>
          restorePeoplePromptRevisionMut.mutateAsync({ ...a, role })
        }
        useDiff={useRoleDiff}
        renderTestPanel={(addendum) => (
          <PeopleRankingTestPanel role={role} addendum={addendum} />
        )}
      />
    </CollapsibleCard>
  );
};

export default PeopleRankingPromptCard;
